import { Injectable } from "@angular/core";
import { FetchStatusSelector, FetchStatusType } from "@obg/ngrx-utils";
import { memoizeSelector } from "@obg/ngx.global";
import { first, isEmpty, map, some } from "lodash-es";

import { CommonState } from "../../state";
import { Offer } from "../campaign.model";

import { ACTION_PREFIX } from "./customer-offer.action";
import { CustomerOffer, FulfillmentStep } from "./customer-offer.model";

// @dynamic
@Injectable({
	providedIn: "root",
})
export class CustomerOfferSelector {
	isBusy() {
		return (state: CommonState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}

	isFail() {
		return (state: CommonState): boolean =>
			FetchStatusSelector.isStatus(ACTION_PREFIX, FetchStatusType.Fail)(state);
	}

	isEnabled() {
		return (state: CommonState): boolean => state.campaignCustomerOffer.isEnabled;
	}

	@memoizeSelector<CommonState>((state) => [state.campaignCustomerOffer.customerOffers])
	getOffers(fulfillmentStep?: FulfillmentStep) {
		return (state: CommonState): Offer[] =>
			this.extractOffers(state.campaignCustomerOffer.customerOffers, fulfillmentStep);
	}

	@memoizeSelector<CommonState>((state) => [state.campaignCustomerOffer.visitorOffers])
	getVisitorOffers(fulfillmentStep?: FulfillmentStep) {
		return (state: CommonState): Offer[] | undefined =>
			state.campaignCustomerOffer.visitorOffers &&
			this.extractOffers(state.campaignCustomerOffer.visitorOffers, fulfillmentStep);
	}

	hasDepositOffers() {
		return (state: CommonState): boolean => state.campaignCustomerOffer.hasDepositOffers;
	}

	hasOffers() {
		return (state: CommonState): boolean => !isEmpty(state.campaignCustomerOffer.customerOffers);
	}

	hasVisitorOffers() {
		return (state: CommonState): boolean => !isEmpty(state.campaignCustomerOffer.visitorOffers);
	}

	private transformOffer(customerOffer: CustomerOffer): Offer {
		const { nextFulfillmentSteps, offer } = customerOffer;
		if (nextFulfillmentSteps && !isEmpty(nextFulfillmentSteps.next)) {
			const firstFulfillment = first(nextFulfillmentSteps.next);
			return {
				...offer,
				fulfillmentId: firstFulfillment?.prefixedFulfillmentId,
			};
		}

		return offer;
	}

	private extractOffers(customerOffers: CustomerOffer[], fulfillmentStep?: FulfillmentStep): Offer[] {
		if (!fulfillmentStep) {
			return map(customerOffers, (x) => this.transformOffer(x));
		}

		return customerOffers
			.filter((x) => some(x.nextFulfillmentSteps && x.nextFulfillmentSteps.next, { type: fulfillmentStep }))
			.map((x) => this.transformOffer(x));
	}
}
